import React, { createContext, useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { Warehouse, Loader2, Plus } from 'lucide-react';
import { useAuth } from './AuthContext';
import api from '../api/client';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WarehouseContext = createContext(null);
const KEY = 'kmt_warehouse';

export function WarehouseProvider({ children }) {
  const { user } = useAuth();
  const [warehouses, setWarehouses] = useState([]);
  const [activeId, setActiveId] = useState(() => localStorage.getItem(KEY) || '');
  const [ready, setReady] = useState(false);

  const canSwitch = ['ADMIN', 'SUPERVISOR'].includes(user?.role);

  const applyHeader = (id) => {
    if (id) axios.defaults.headers.common['X-Warehouse-Id'] = id;
    else delete axios.defaults.headers.common['X-Warehouse-Id'];
  };

  const setActive = (id) => {
    setActiveId(id);
    localStorage.setItem(KEY, id || '');
    applyHeader(id);
  };

  const refresh = () => api.listWarehouses().then((w) => { setWarehouses(w); return w; }).catch(() => []);

  useEffect(() => {
    if (!user) { setReady(false); return; }
    let cancelled = false;
    (async () => {
      const list = await refresh();
      if (cancelled) return;
      // Operators (and any non-manager) are locked to their assigned warehouse.
      if (!canSwitch && user.warehouse_id) {
        setActive(user.warehouse_id);
      } else {
        const stored = localStorage.getItem(KEY) || '';
        const valid = list.some((w) => w.id === stored);
        if (valid) applyHeader(stored);
        else { setActiveId(''); applyHeader(''); }
      }
      setReady(true);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [user?.id]);

  const active = warehouses.find((w) => w.id === activeId) || null;
  const needsChoice = ready && canSwitch && !active;

  return (
    <WarehouseContext.Provider value={{ warehouses, activeId, active, setActive, canSwitch, needsChoice, ready, refresh }}>
      {children}
    </WarehouseContext.Provider>
  );
}

export const useWarehouse = () => useContext(WarehouseContext);

export function ChooseWarehouse() {
  const { warehouses, setActive } = useWarehouse();
  return (
    <div className="kmt-grid-bg min-h-screen flex flex-col items-center justify-center px-4" data-testid="choose-warehouse">
      <div className="kmt-panel p-8 max-w-lg w-full">
        <div className="flex items-center gap-3 mb-1">
          <Warehouse size={22} style={{ color: 'var(--kmt-gold)' }} />
          <div className="font-display text-2xl font-semibold">Choose a Factory</div>
        </div>
        <p className="text-sm mb-6" style={{ color: 'var(--kmt-muted)' }}>Select which factory / warehouse you want to work in. All data you see stays within that factory.</p>
        {warehouses.length === 0 ? (
          <div className="text-sm" style={{ color: 'var(--kmt-muted)' }}>No factories yet. Loading…</div>
        ) : (
          <div className="space-y-2">
            {warehouses.map((w) => (
              <button key={w.id} onClick={() => setActive(w.id)} data-testid={`choose-wh-${w.id}`}
                className="w-full text-left rounded-lg px-4 py-3 flex items-center justify-between transition-colors hover:bg-[#26262c]"
                style={{ background: 'var(--kmt-panel-2)', border: '1px solid var(--kmt-border)' }}>
                <div>
                  <div className="font-semibold">{w.name}</div>
                  {w.location && <div className="text-xs" style={{ color: 'var(--kmt-muted)' }}>{w.location}</div>}
                </div>
                <span className="font-mono-k text-xs" style={{ color: 'var(--kmt-gold)' }}>{w.id}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
